
/**
 * Utility functions for authentication management
 */
import { supabase } from "@/integrations/supabase/client";
import { ThemeLoader } from "@/components/theme/ThemeLoader";

/**
 * Thoroughly cleans up authentication state from local storage
 * This helps prevent "limbo" states where users can't properly log out
 * or authentication gets stuck in an inconsistent state
 */
export const cleanupAuthState = () => {
  console.log("Cleaning up auth state...");
  
  // Remove standard auth tokens
  localStorage.removeItem('supabase.auth.token');
  
  // Remove all Supabase auth keys from localStorage
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      console.log(`Removing auth key: ${key}`);
      localStorage.removeItem(key);
    }
  });
  
  // Remove from sessionStorage if in use
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      console.log(`Removing auth key from sessionStorage: ${key}`);
      sessionStorage.removeItem(key);
    }
  });

  // Clear user themes to prevent theme bleed between users
  ThemeLoader.clearUserTheme();
};

/**
 * Enhanced session check that works reliably on mobile and desktop
 */
export const enhancedSessionCheck = async () => {
  try {
    // First try to get session from local storage
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.log("No session found in storage");
      return null;
    }
    
    // Try to refresh the session explicitly
    const { data: refreshData, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error("Session refresh failed:", error);
      if (error.message?.includes('refresh_token')) {
        // Clean up auth state if refresh token is invalid
        cleanupAuthState();
        return null;
      }
    }
    
    return refreshData.session;
  } catch (error) {
    console.error("Error during session check:", error);
    return null;
  }
};
